import { useMemo } from 'react';
import { Semester, SemesterCourse } from '../../../api/requests/curriculum';
import { getColor } from '../utils';
import {
  CoursesBySemestersExtraCourse,
  CourseStatusTooltip,
  ValidationType,
} from '../validation-type';
import Course, { CourseProps } from './Course';
import { useTranslation } from 'react-i18next';
import { CourseStatusTypes } from '../../../types/curricularAdvancementTypes';

type ProgressionBySemesterProps = {
  title: string;
  semesterCourses: Semester['semesterCourses'];
  onClickInfoModal?: (row: SemesterCourse) => void;
  extraCourses?: CoursesBySemestersExtraCourse[];
};

const ProgressionBySemester = ({
  title,
  semesterCourses,
  onClickInfoModal,
  extraCourses,
}: ProgressionBySemesterProps) => {
  const prefix = 'statusSemesterCourseTooltip';
  const prefixDegreeActivity = 'degreeActivity';
  const { t } = useTranslation();

  const convalidationToolTip = useMemo(() => {
    return ({
      validationType,
      examGrade,
      finalGrade,
      externalCourseName,
      externalGrade,
      code,
    }: CourseStatusTooltip) =>
      t(`${prefix}.${validationType}`, {
        examGrade,
        finalGrade,
        externalCourseName,
        externalGrade,
        code,
      });
  }, [t, prefix]);

  /**
   * Si el curso es obligatorio o actividad la
   * informacion se toma de `course`
   * de lo contrario se toma desde `optionalSlot`
   */
  const courses = useMemo<
    Array<Omit<CourseProps, 'onClick'> & { key: number }>
  >(() => {
    return semesterCourses.map((semesterCourse) => {
      let onClick = undefined;
      if (
        onClickInfoModal &&
        semesterCourse.state === 'A' &&
        semesterCourse.isValidated
      ) {
        onClick = () => onClickInfoModal(semesterCourse);
      }
      if (semesterCourse.course) {
        return {
          key: semesterCourse.id,
          name: semesterCourse.course.name,
          code: semesterCourse.course.code,
          shortening: semesterCourse.course.shortening,
          credits: semesterCourse.course.credits,
          color: getColor(
            semesterCourse.state === 'A' && semesterCourse.isValidated
              ? semesterCourse?.validation?.validationType ??
                  ValidationType.Internal
              : semesterCourse.state,
          ),
          examGrade: semesterCourse.validation?.examGrade,
          finalGrade: semesterCourse.validation?.finalGrade,
          toolTip:
            semesterCourse.state === 'A' && semesterCourse.isValidated
              ? convalidationToolTip({
                  validationType:
                    semesterCourse?.validation?.validationType ??
                    ValidationType.Internal,
                  validationSubType:
                    semesterCourse?.validation?.validationSubType,
                  examGrade: semesterCourse.validation?.examGrade,
                  finalGrade: semesterCourse.validation?.finalGrade,
                  externalGrade: semesterCourse.validation?.externalGrade,
                  externalCourseName:
                    semesterCourse.validation?.externalCourseName,
                  code: semesterCourse.validation?.code,
                })
              : '',
          onClick,
        };
      } else {
        return {
          key: semesterCourse.id,
          name: semesterCourse.courseType.name,
          shortening: '',
          credits: semesterCourse.optionalSlot?.credits,
          color: getColor(semesterCourse.state),
          examGrade: semesterCourse.validation?.examGrade,
          finalGrade: semesterCourse.validation?.finalGrade,
          toolTip: '',
          onClick,
        };
      }
    });
  }, [onClickInfoModal, semesterCourses, convalidationToolTip]);

  return (
    <div>
      <div className="d-block text-center mb-3 px-1">{title}</div>
      <div
        className="d-flex"
        style={{
          flexDirection: 'column',
          gap: 16,
        }}
      >
        {courses.map((semesterCourse) => (
          <Course {...semesterCourse} />
        ))}
        {extraCourses?.map((extraCourse) => {
          const id = Math.random().toString(36).substr(2, 9);
          return (
            <Course
              name={extraCourse.name}
              color={getColor(
                isCourseStatusType(extraCourse.state)
                  ? extraCourse.state
                  : undefined,
              )}
              replaceCredits={t(`${prefixDegreeActivity}.degreeActivityType`)}
              toolTip={t(`${prefixDegreeActivity}.degreeActivityTooltip`)}
              toolTipId={`extracourse-${id}`}
            />
          );
        })}
      </div>
    </div>
  );
};

function isCourseStatusType(value?: string): value is CourseStatusTypes {
  return ['E', 'A', 'P', 'R', 'V', 'VS', 'VV'].includes(
    value as CourseStatusTypes,
  );
}

export default ProgressionBySemester;
